import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate, formatCurrency } from '../../utils/format';
import { peakOf } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge
} from '../../components/ui';
import { meetingSummary, meetingStatusOf } from './analytics';
import type { MeetingLike, MinuteLike, ActionLike } from './analytics';

export interface MeetingSummaryProps {
  meetings: MeetingLike[];
  minutes: MinuteLike[];
  actions: ActionLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

/** The Meeting Management module's Summary tab: tiles, then report panels and watch-lists. */
const MeetingSummaryView = ({ meetings, minutes, actions, loading, isDarkMode }: MeetingSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => meetingSummary(meetings, minutes, actions), [meetings, minutes, actions]);
  const monthPeak = peakOf(s.months, (r) => r.count);
  const statusPeak = peakOf(s.byStatus, (r) => r.count);
  const resolutionPeak = peakOf(s.resolutionsByStatus, (r) => r.count);
  const personPeak = peakOf(s.byPerson, (r) => r.open);
  const statusColor = (name: string) =>
    (name === 'Completed' || name === 'Approved' ? t.income
      : name === 'Cancelled' || name === 'Rejected' ? t.danger
        : name === 'Deferred' ? t.warning : t.info);

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Meetings This Year" value={String(s.meetingsThisYear)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Upcoming (60 d)" value={String(s.upcoming.length)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Resolutions" value={String(s.resolutions)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Approved" value={String(s.approved)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Budget Approved (Year)" value={formatCurrency(s.budgetApprovedThisYear)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Open Actions" value={String(s.openActions)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Due in 7 Days" value={String(s.dueSoon)} color={s.dueSoon ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Overdue Actions" value={String(s.overdue.length)} color={s.overdue.length ? t.danger : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Actions Completed" value={s.actionTotal ? `${s.completionRate}%` : '—'} color={t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <TablePanel
        title="Meetings by Month"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="420px"
        columns={[
          { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
          { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={monthPeak} color={t.info} isDarkMode={isDarkMode} /> },
          { key: 'count', header: 'Meetings', align: 'right' }
        ]}
        rows={s.months}
        rowKey="month"
        emptyMessage="No meetings recorded yet."
        below={<TotalRow label="Total, last 12 months" value={s.months.reduce((n, m) => n + m.count, 0)} isDarkMode={isDarkMode} />}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Meetings by Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Status', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={statusPeak} color={statusColor(r.name)} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Meetings', align: 'right' }
          ]}
          rows={s.byStatus}
          rowKey="name"
          emptyMessage="No meetings recorded yet."
        />
        <TablePanel
          title="Meetings by Committee"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Committee', render: (r) => <strong>{r.name}</strong> },
            { key: 'count', header: 'Meetings', align: 'right' }
          ]}
          rows={s.byCommittee}
          rowKey="name"
          emptyMessage="No meetings recorded yet."
        />
      </SummaryColumns>

      <TablePanel
        title="Resolutions by Status"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="420px"
        columns={[
          { key: 'name', header: 'Status', render: (r) => <strong>{r.name}</strong> },
          { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={resolutionPeak} color={statusColor(r.name)} isDarkMode={isDarkMode} /> },
          { key: 'count', header: 'Resolutions', align: 'right' },
          { key: 'total', header: 'Budget', align: 'right', nowrap: true, render: (r) => formatCurrency(r.total) }
        ]}
        rows={s.resolutionsByStatus}
        rowKey="name"
        emptyMessage="No minutes or resolutions recorded yet."
        below={s.resolutions ? <TotalRow label="Budget approved (all time)" value={formatCurrency(s.budgetApproved)} color={t.income} isDarkMode={isDarkMode} /> : null}
      />

      <TablePanel
        title="Coming Up — Next 60 Days"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'date', header: 'Date', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
          { key: 'time', header: 'Time', nowrap: true, render: (r) => r.time || '-' },
          { key: 'title', header: 'Meeting', render: (r) => r.title || '-' },
          { key: 'committee', header: 'Committee', render: (r) => r.committee || '-' },
          { key: 'venue', header: 'Venue', render: (r) => r.venue || '-' }
        ]}
        rows={s.upcoming}
        rowKey={(r) => `${r.recordId}|${r.date}`}
        emptyMessage="No meetings scheduled in the next 60 days."
      />

      <TablePanel
        title="Action Items per Person"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="480px"
        columns={[
          { key: 'name', header: 'Assigned To', render: (r) => <strong>{r.name}</strong> },
          { key: 'bar', header: 'Open', render: (r) => <BarCell value={r.open} max={personPeak} color={r.overdue ? t.danger : t.primary} isDarkMode={isDarkMode} /> },
          { key: 'open', header: 'Open', align: 'right' },
          { key: 'overdue', header: 'Overdue', align: 'right', render: (r) => (r.overdue ? <strong style={{ color: t.danger }}>{r.overdue}</strong> : 0) },
          { key: 'done', header: 'Done', align: 'right' },
          { key: 'total', header: 'Total', align: 'right' }
        ]}
        rows={s.byPerson}
        rowKey="name"
        emptyMessage="No action items recorded yet."
        below={s.actionTotal ? <TotalRow label="Open action items" value={`${s.openActions} of ${s.actionTotal}`} isDarkMode={isDarkMode} /> : null}
      />

      <TablePanel
        title="Overdue Action Items"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'task', header: 'Task', wrap: true, render: (r) => <strong>{r.task || '-'}</strong> },
          { key: 'topic', header: 'Resolution', wrap: true, render: (r) => r.topic || r.minId || '-' },
          { key: 'assignedTo', header: 'Assigned To', render: (r) => r.assignedTo || '-' },
          { key: 'dueDate', header: 'Due', nowrap: true, render: (r) => formatDate(r.dueDate) },
          { key: 'daysOverdue', header: 'Late', nowrap: true, render: (r) => <Badge tone="error">{r.daysOverdue} d overdue</Badge> }
        ]}
        rows={s.overdue}
        rowKey={(r) => `${r.recordId}|${r.task}|${r.dueDate}`}
        emptyMessage="No action item is past its due date."
      />

      {s.notClosed.length > 0 && (
        <TablePanel
          title="Past Meetings Still Marked Scheduled"
          isDarkMode={isDarkMode}
          columns={[
            { key: 'date', header: 'Date', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
            { key: 'title', header: 'Meeting', render: (r) => r.title || '-' },
            { key: 'committee', header: 'Committee', render: (r) => r.committee || '-' },
            { key: 'meetingStatus', header: 'Status', render: (r) => <Badge tone="warning">{meetingStatusOf(r)}</Badge> }
          ]}
          rows={s.notClosed}
          rowKey={(r) => `${r.recordId}|${r.date}`}
        />
      )}
    </>
  );
};

export default MeetingSummaryView;
